import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@design-system';

import { Loader2, Send, UserPlus } from 'lucide-react';

import {
  AgentEnteredWaitingCard,
  PrefillDateField,
  useFillIntakeForm,
} from '@/features/invite-client';
import { PhoneNumberField } from '@/components/PhoneNumberField';

export function FillIntakeFormTab() {
  const {
    form,
    onSubmit,
    isLoading,
    isAgentEntered,
    isConsumerSmsMfaAvailable,
    phoneCountryCode,
    setPhoneCountryCode,
    phoneNumber,
    setPhoneNumber,
    isPhoneTouched,
    touchPhone,
    phoneError,
    canSubmit,
    agentEnteredIntakeId,
    startOver,
    handleConsentStatusChange,
  } = useFillIntakeForm();

  // Once a consent request exists there is nothing useful for the advisor to do on the form, and
  // leaving it visible would suggest submitting again would help.
  if (agentEnteredIntakeId) {
    return (
      <div className="space-y-6">
        <AgentEnteredWaitingCard
          requestId={agentEnteredIntakeId}
          onDismiss={startOver}
          onConsentStatusChange={handleConsentStatusChange}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-base font-semibold text-gray-950">Fill Intake Form for Client</h4>
        <p className="text-foreground text-sm">
          {isAgentEntered
            ? "Enter your client's details. We'll email them for permission before you can fill out the form on their behalf."
            : "Enter your client's name to add them and complete the intake form on their behalf. No invitation will be sent."}
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={onSubmit}
          className="space-y-6"
        >
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="John"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Doe"
                      disabled={isLoading}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isAgentEntered && (
              <>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Client email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john@example.com"
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {isConsumerSmsMfaAvailable && (
                  <PhoneNumberField
                    id="agent-entered-client-phone"
                    name="agent-entered-client-phone-manual-entry"
                    value={{ countryCode: phoneCountryCode, phoneNumber }}
                    onChange={{
                      countryCode: setPhoneCountryCode,
                      phoneNumber: setPhoneNumber,
                    }}
                    validation={{
                      invalid: isPhoneTouched && phoneError !== null,
                      error:
                        isPhoneTouched && phoneError ? (
                          <p
                            role="alert"
                            className="text-destructive text-sm"
                          >
                            {phoneError}
                          </p>
                        ) : undefined,
                      onBlur: touchPhone,
                    }}
                    options={{ disabled: isLoading, required: true, showHelper: false }}
                  />
                )}
                <FormField
                  control={form.control}
                  name="birthDate"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      {/* Your client re-enters this to prove it's them, so it has to be right. */}
                      <PrefillDateField
                        id="agent-entered-client-birth-date"
                        label="Client date of birth"
                        value={field.value}
                        error={fieldState.error?.message}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />
              </>
            )}
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isAgentEntered ? 'Sending...' : 'Creating...'}
              </>
            ) : isAgentEntered ? (
              <>
                <Send className="size-4" />
                Request Client Consent
              </>
            ) : (
              <>
                <UserPlus className="size-4" />
                Add Client &amp; Open Form
              </>
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
